import { useCallback, useEffect, useRef, useState } from 'react';

import type { SliderProps } from '@change/ds-forms';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';

import { useRealtimeValidation } from '../../../../../container/hooks/useRealtimeValidation';
import { useAmountConverter } from '../../../../../hooks/useAmountConverter';
import { useMembershipModalState } from '../../../../../modalContext';

import { useAmountFormatters } from './useAmountFormatters';

type Result = ModelHookResult<
	{
		amount: number;
		ariaLabel: string;
		currency: string;
		currencySymbol: string;
		errorMessage: string | null;
		placeholder: string;
		minValue: number;
		maxValue: number;
		sliderStep: number;
		sliderAmount: number;
		sliderLayout: SliderProps['layout'];
	},
	{
		formatValue: (value: number) => string;
		formatTooltipValue: (value: number) => string;
		onSliderChange: (value: number) => void;
		onSliderChangeEnd: (value: number) => void;
		onInputChange: (value: string) => void;
		onInputFocus: () => void;
	}
>;

export function useSliderAmountSelector(): Result {
	const [{ amount, currencyCode, validationErrors, sliderConfig }, { setAmount }] = useMembershipModalState();
	const { getCurrencySymbol, translate } = useI18n();
	const { formatValue, formatTooltipValue } = useAmountFormatters(currencyCode);

	const lastFocusedAmount = useRef<number>(0);
	const [dynamicMaxValue, setDynamicMaxValue] = useState<number>(Math.max(sliderConfig.maxSliderAmount, amount));
	const [sliderAmount, setSliderAmount] = useState<number>(amount);
	const [isCustomInput, setIsCustomInput] = useState<boolean>(
		(amount - sliderConfig.minSliderAmount) % sliderConfig.sliderStep !== 0,
	);
	const track = useTracking();
	const { toAmountUsd } = useAmountConverter();

	useRealtimeValidation();

	const currencySymbol = getCurrencySymbol(currencyCode);

	useEffect(() => {
		void track('membership_ask_slider_selector_viewed', {
			local_amount: amount,
			amount: toAmountUsd(amount),
		});
	}, []);

	return {
		data: {
			amount,
			// TODO: Translate this in a separate PR
			ariaLabel: 'Amount',
			currency: currencyCode,
			currencySymbol,
			errorMessage: isCustomInput ? (validationErrors?.amount ?? null) : null,
			placeholder: translate('corgi.co.promote.custom_amount'),
			maxValue:
				sliderConfig.minSliderAmount +
				Math.ceil((dynamicMaxValue - sliderConfig.minSliderAmount) / sliderConfig.sliderStep) * sliderConfig.sliderStep,
			minValue: sliderConfig.minSliderAmount,
			sliderStep: isCustomInput ? 1 : sliderConfig.sliderStep,
			sliderAmount,
			sliderLayout: sliderConfig.sliderLayout,
		},
		actions: {
			formatValue,
			formatTooltipValue,
			onSliderChange: useCallback(
				(value: number) => {
					setIsCustomInput(false);
					setSliderAmount(value);
				},
				[setSliderAmount],
			),
			onSliderChangeEnd: useCallback(
				(value: number) => {
					setAmount(value);
					lastFocusedAmount.current = value;
					void track('membership_ask_slider_amount_changed', {
						amount: toAmountUsd(value),
						local_amount: value,
					});
				},
				[setAmount, track, toAmountUsd],
			),
			onInputChange: useCallback(
				(value: string) => {
					if (/^\d*$/.test(value)) {
						const newAmount = Number(value);
						if (newAmount > 0) {
							setIsCustomInput(true);
							setAmount(newAmount);
							lastFocusedAmount.current = newAmount;
							setSliderAmount(newAmount);
							if (newAmount > dynamicMaxValue) setDynamicMaxValue(newAmount);
							void track('membership_ask_slider_amount_input_changed', {
								amount: toAmountUsd(newAmount),
								local_amount: newAmount,
							});
						} else if (value === '0') {
							setIsCustomInput(true);
							setAmount(0);
							setSliderAmount(sliderConfig.minSliderAmount);
						} else if (value === '') {
							setIsCustomInput(false);
							setAmount(0);
							setSliderAmount(sliderConfig.minSliderAmount);
							lastFocusedAmount.current = 0;
						}
					}
				},
				[setAmount, dynamicMaxValue, sliderConfig, track, toAmountUsd],
			),
			onInputFocus: useCallback(() => {
				void track('membership_ask_slider_amount_input_focused', {
					amount: toAmountUsd(lastFocusedAmount.current),
					local_amount: lastFocusedAmount.current,
				});
			}, [track, toAmountUsd]),
		},
	};
}
